import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import vuetify from './plugins/vuetify'
import MiniAlert from '@/components/Alerts/MiniAlerts'
import EncryptValue from '@/plugins/encrypt'
import currency from '@/plugins/currency'
import ExcelValidator from '@/plugins/excelValidator'
import msal from 'vue-msal'
import VueCookies from 'vue-cookies'

import {
  ValidationObserver,
  ValidationProvider
} from 'vee-validate'

import './registerServiceWorker'
import cleanCacheOnNewBuild from '@/plugins/cleanCache'
import * as VueGoogleMaps from 'vue2-google-maps'

import { SearchBar } from '@internal_projects_dd/search-bar'
import { InputNumber } from '@internal_projects_dd/input-number'
import AppLauncherButton from '@internal_projects_dd/app-launcher-button'

import keycloak from './keycloak'


/*
 * =========================================================
 * LOGS
 * =========================================================
 */

if (process.env.VUE_APP_NODE_ENV === 'production') {
  console.log = function () {}
  console.info = function () {}
  console.debug = function () {}
}


cleanCacheOnNewBuild()


Vue.config.productionTip = false


/*
 * =========================================================
 * OLYMPUS
 * =========================================================
 *
 * Sempre que a sessão terminar ou não existir,
 * o utilizador deve regressar ao Olympus.
 *
 * Nunca abrimos diretamente a página de login
 * do Keycloak a partir do ODC.
 */
const OLYMPUS_URL = (
  process.env.VUE_APP_LAUNCHER_URL || ''
).replace(/\/+$/, '')


const redirectToOlympus = () => {
  if (!OLYMPUS_URL) {
    console.error(
      'VUE_APP_LAUNCHER_URL não está configurado.'
    )

    return
  }

  window.location.href = OLYMPUS_URL
}


/*
 * =========================================================
 * PROTOTYPES
 * =========================================================
 */

Vue.prototype.$encryptValue = EncryptValue

Vue.prototype.$currency = currency

Vue.prototype.$excelValidator = ExcelValidator


/*
 * =========================================================
 * COMPONENTES GLOBAIS
 * =========================================================
 */

Vue.component(
  'SearchBar',
  SearchBar
)

Vue.component(
  'InputNumber',
  InputNumber
)

Vue.component(
  'ValidationProvider',
  ValidationProvider
)

Vue.component(
  'ValidationObserver',
  ValidationObserver
)

Vue.component(
  'AppLauncherButton',
  AppLauncherButton
)


/*
 * =========================================================
 * PLUGINS
 * =========================================================
 */

// Comments
Vue.use(
  'MiniAlert',
  MiniAlert
)


Vue.use(
  VueCookies,
  {
    expires: '7d',
    secure: true
  }
)


Vue.use(
  msal,
  {
    auth: {
      clientId:
        process.env.VUE_APP_MICROSOFT
    }
  }
)


Vue.use(
  VueGoogleMaps,
  {
    load: {
      key:
        process.env.VUE_APP_GOOGLE_MAPS_KEY,

      libraries:
        'places'
    },

    installComponents:
      true
  }
)


/*
 * =========================================================
 * UNHANDLED PROMISE REJECTIONS
 * =========================================================
 *
 * Evita erros no browser quando existem vários pedidos
 * simultâneos e a sessão/token expira.
 */
window.addEventListener(
  'unhandledrejection',

  (event) => {
    const statusCode =
      event?.reason?.response?.status ?? null

    const isHandledSessionExpiry =
      event?.reason?.__sessionExpiredHandled === true

    if (
      isHandledSessionExpiry ||
      statusCode === 401 ||
      statusCode === 419
    ) {
      event.preventDefault()
    }
  }
)


/*
 * =========================================================
 * KEYCLOAK
 * =========================================================
 *
 * IMPORTANTE:
 *
 * Não usamos:
 *
 *   onLoad: 'login-required'
 *
 * porque isso faria o ODC abrir diretamente
 * o login do Keycloak.
 *
 * Com check-sso apenas verificamos se existe
 * uma sessão válida.
 *
 * Se não existir, regressamos ao Olympus.
 */
keycloak
  .init({
    onLoad: 'check-sso',

    /*
     * Desativado para evitar o iframe interno
     * do Keycloak estar constantemente
     * a verificar a sessão.
     */
    checkLoginIframe: false
  })
  .then((authenticated) => {

    /*
     * =====================================================
     * SEM SESSÃO
     * =====================================================
     *
     * O ODC não inicia diretamente um login Keycloak.
     *
     * Regressamos ao Olympus.
     */
    if (!authenticated) {
      redirectToOlympus()

      return
    }


    /*
     * =====================================================
     * TOKEN EXPIRADO
     * =====================================================
     *
     * Quando o access token expira tentamos renovar
     * através do refresh token.
     *
     * Só regressamos ao Olympus se a renovação falhar.
     */
    keycloak.onTokenExpired = async () => {
      try {
        const refreshed =
          await keycloak.updateToken(30)

        /*
         * Se não houve refresh é porque o token
         * ainda estava dentro da validade necessária.
         */
        if (!refreshed) {
          return
        }
      } catch (error) {
        console.error(
          'Não foi possível renovar a sessão do Keycloak:',
          error
        )

        /*
         * Refresh token expirou,
         * sessão Keycloak terminou,
         * ou ocorreu outro erro de autenticação.
         *
         * Voltamos ao Olympus.
         */
        redirectToOlympus()
      }
    }


    /*
     * =====================================================
     * VUE
     * =====================================================
     */

    new Vue({
      router,
      store,
      vuetify,

      render: h => h(App)
    }).$mount('#app')


    /*
     * =====================================================
     * ROUTER
     * =====================================================
     *
     * Depois de validar a sessão Keycloak,
     * garantimos que as páginas antigas
     * de entrada do ODC não ficam visíveis.
     */
    router.onReady(() => {

      const currentPath =
        router.currentRoute?.path

      if (
        authenticated &&
        [
          '/',
          '/login'
        ].includes(currentPath)
      ) {
        router
          .replace('/dashboard')
          .catch(() => {})
      }
    })
  })
  .catch((error) => {

    console.error(
      'Erro ao iniciar Keycloak:',
      error
    )

    /*
     * Se o próprio Keycloak não conseguir
     * inicializar, não mostramos diretamente
     * a página de login Keycloak.
     *
     * Voltamos ao Olympus.
     */
    redirectToOlympus()
  })